import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listVideos } from '../../api/videos';
import { DailyStatsChart } from './DailyStatsChart';
import { AudienceDemographics } from './AudienceDemographics';

interface AdminAd {
  _id: string;
  title: string;
  description: string;
  categories?: string[];
  tags?: string[];
  views?: number;
  clicks?: number;
  analytics?: {
    telemetrySamples?: number;
    telemetryBlankEvents?: number;
    social?: {
      totalClicks?: number;
      bySource?: Record<string, number>;
    };
  };
}

type AnalyticsListVideosResponse =
  | AdminAd[]
  | {
      items: AdminAd[];
      total?: number;
    }
  | null
  | undefined;

const PAGE_SIZE = 20;

interface AnalyticsProps {
  userId?: string;
  title?: string;
}

export const Analytics: React.FC<AnalyticsProps> = ({ userId, title = 'Ad Analytics' }) => {
  const [metric, setMetric] = useState<'views' | 'clicks' | 'telemetryBlankEvents'>('views');
  const [category, setCategory] = useState('');
  const [tag, setTag] = useState('');
  const [timeRange, setTimeRange] = useState<'all' | '7d' | '30d'>('all');
  const [ads, setAds] = useState<AdminAd[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demographicAdId, setDemographicAdId] = useState<string>('');
  const [search, setSearch] = useState('');

  // Filter ads by search query (case-insensitive title match)
  const filteredAds = search.trim()
    ? ads.filter((ad) =>
        (ad.title ?? '').toLowerCase().includes(search.trim().toLowerCase()),
      )
    : ads;

  const metricValue = (ad: AdminAd): number => {
    if (metric === 'views') return typeof ad.views === 'number' ? ad.views : 0;
    if (metric === 'clicks') return typeof ad.clicks === 'number' ? ad.clicks : 0;
    return ad.analytics?.telemetryBlankEvents ?? 0;
  };

  const totalViews = filteredAds.reduce((sum, ad) => sum + (ad.views ?? 0), 0);
  const totalClicks = filteredAds.reduce((sum, ad) => sum + (ad.clicks ?? 0), 0);
  const totalBlanks = filteredAds.reduce((sum, ad) => sum + (ad.analytics?.telemetryBlankEvents ?? 0), 0);
  const totalSocialClicks = filteredAds.reduce(
    (sum, ad) => sum + (ad.analytics?.social?.totalClicks ?? 0),
    0,
  );
  const averageCtr = totalViews > 0 ? (totalClicks / totalViews) * 100 : null;
  const maxMetricValue = filteredAds.reduce((max, ad) => {
    const value = metricValue(ad);
    return value > max ? value : max;
  }, 0);

  // Limit chart to a reasonable number of bars for readability
  const chartAds = filteredAds.slice(0, 8);

  const dailyRangeFrom: string | undefined = (() => {
    const now = new Date();
    if (timeRange === '7d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return d.toISOString().slice(0, 10);
    }
    if (timeRange === '30d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d.toISOString().slice(0, 10);
    }
    return undefined;
  })();

  useEffect(() => {
    let cancelled = false;

    const fetchTopAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        let createdAfter: string | undefined;
        if (timeRange === '7d') {
          const d = new Date(now);
          d.setDate(d.getDate() - 7);
          createdAfter = d.toISOString();
        } else if (timeRange === '30d') {
          const d = new Date(now);
          d.setDate(d.getDate() - 30);
          createdAfter = d.toISOString();
        }

        const data = (await listVideos({
          category: category || undefined,
          tag: tag || undefined,
          userId: userId || undefined,
          createdAfter,
          sortBy: metric === 'telemetryBlankEvents' ? 'clicks' : metric,
          sortDir: 'desc',
          page: 1,
          pageSize: PAGE_SIZE,
        })) as AnalyticsListVideosResponse;

        if (cancelled) return;

        if (Array.isArray(data)) {
          setAds(data as AdminAd[]);
        } else if (data && !Array.isArray(data) && Array.isArray(data.items)) {
          setAds(data.items as AdminAd[]);
        } else {
          setAds([]);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          let message =
            err instanceof Error && typeof err.message === 'string'
              ? err.message
              : 'Analytics data is not available right now.';

          const lower = message.toLowerCase();
          if (lower.includes('request failed') || lower.includes('network error')) {
            message =
              'Analytics data is not available in this environment yet. Your campaigns and admin tools still work as normal.';
          }

          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchTopAds();

    return () => {
      cancelled = true;
    };
  }, [metric, category, tag, userId, timeRange]);

  return (
    <div className="admin-analytics">
      <h2>{title}</h2>

      {/* Audience Demographics — privacy-safe aggregate counts */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            Demographics for:
            <select
              value={demographicAdId}
              onChange={(e) => setDemographicAdId(e.target.value)}
              style={{ marginLeft: '0.5rem', fontSize: 12 }}
            >
              <option value="">All ads (aggregate)</option>
              {filteredAds.map((ad) => (
                <option key={ad._id} value={ad._id}>
                  {ad.title || 'Untitled'}
                </option>
              ))}
            </select>
          </label>
        </div>
        <AudienceDemographics
          hours={24}
          pollInterval={15_000}
          adId={demographicAdId || undefined}
        />
      </div>

      <p>
        Top ads by
        {metric === 'views'
          ? ' views'
          : metric === 'clicks'
            ? ' clicks'
            : ' safety blanking events'}
        , filterable by category and tag.
      </p>

      <div
        style={{
          margin: '1rem 0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div
          style={{
            flex: '1 1 140px',
            padding: '0.5rem 0.75rem',
            borderRadius: 8,
            background: '#0f172a',
            color: '#e5e7eb',
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.8 }}>Total views</div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{totalViews}</div>
        </div>
        <div
          style={{
            flex: '1 1 140px',
            padding: '0.5rem 0.75rem',
            borderRadius: 8,
            background: '#0f172a',
            color: '#e5e7eb',
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.8 }}>Total clicks</div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{totalClicks}</div>
        </div>
        <div
          style={{
            flex: '1 1 140px',
            padding: '0.5rem 0.75rem',
            borderRadius: 8,
            background: '#0f172a',
            color: '#e5e7eb',
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.8 }}>Avg CTR</div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {averageCtr !== null ? `${averageCtr.toFixed(1)}%` : '–'}
          </div>
        </div>
        <div
          style={{
            flex: '1 1 160px',
            padding: '0.5rem 0.75rem',
            borderRadius: 8,
            background: '#0f172a',
            color: '#e5e7eb',
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.8 }}>Social clicks (all sources)</div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{totalSocialClicks}</div>
        </div>
        {metric === 'telemetryBlankEvents' && (
          <div
            style={{
              flex: '1 1 160px',
              padding: '0.5rem 0.75rem',
              borderRadius: 8,
              background: '#0f172a',
              color: '#e5e7eb',
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.8 }}>Safety blanks (total)</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{totalBlanks}</div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label>
          Metric:
          <select
            value={metric}
            onChange={(e) =>
              setMetric(
                e.target.value === 'clicks'
                  ? 'clicks'
                  : e.target.value === 'telemetryBlankEvents'
                    ? 'telemetryBlankEvents'
                    : 'views',
              )
            }
            style={{ marginLeft: '0.5rem' }}
          >
            <option value="views">Views</option>
            <option value="clicks">Clicks</option>
            <option value="telemetryBlankEvents">Safety blanks</option>
          </select>
        </label>

        <label style={{ marginLeft: '0.75rem' }}>
          Time range:
          <select
            value={timeRange}
            onChange={(e) =>
              setTimeRange(
                e.target.value === '7d' ? '7d' : e.target.value === '30d' ? '30d' : 'all',
              )
            }
            style={{ marginLeft: '0.5rem' }}
          >
            <option value="all">All time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </label>

        <input
          type="text"
          placeholder="Search ads by title"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            marginLeft: '0.5rem',
            padding: '4px 10px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            minWidth: 200,
          }}
        />
        <input
          type="text"
          placeholder="Category filter"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ marginLeft: '0.5rem' }}
        />
        <input
          type="text"
          placeholder="Tag filter"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          style={{ marginLeft: '0.5rem' }}
        />
      </div>

      {loading && <p>Loading analytics...</p>}
      {error && (
        <p style={{ color: '#4b5563', fontSize: 12 }}>
          {error}
        </p>
      )}

      {!loading && !error && chartAds.length > 0 && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: 12,
            background: '#0f172a',
            color: '#e5e7eb',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}
          >
            <span style={{ fontSize: 12, opacity: 0.8 }}>
              Top {chartAds.length} ads by
              {metric === 'views' ? ' views' : metric === 'clicks' ? ' clicks' : ' safety blanks'}
            </span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>
              Each bar shows the relative{' '}
              {metric === 'views' ? 'views' : metric === 'clicks' ? 'clicks' : 'blanks'} per ad.
            </span>
          </div>
          <svg viewBox="0 0 400 150" width="100%" height="150" role="img">
            <desc>Bar chart of top ads by selected metric</desc>
            {/* Baseline */}
            <line x1="0" y1="120" x2="400" y2="120" stroke="#374151" strokeWidth="1" />
            {chartAds.map((ad, index) => {
              const count = metricValue(ad);
              const safeMax = maxMetricValue > 0 ? maxMetricValue : 1;
              const barAreaWidth = 400 / chartAds.length;
              const barWidth = Math.max(12, barAreaWidth - 12);
              const x = index * barAreaWidth + (barAreaWidth - barWidth) / 2;
              const maxBarHeight = 90;
              const barHeight = (count / safeMax) * maxBarHeight;
              const y = 120 - barHeight;
              const label = ad.title?.length > 14 ? `${ad.title.slice(0, 11)}…` : ad.title;

              return (
                <g key={ad._id}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    rx={4}
                    fill={metric === 'telemetryBlankEvents' ? '#f97316' : '#3b82f6'}
                  />
                  <text
                    x={x + barWidth / 2}
                    y={y - 4}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#e5e7eb"
                  >
                    {count}
                  </text>
                  <text
                    x={x + barWidth / 2}
                    y={132}
                    textAnchor="middle"
                    fontSize="9"
                    fill="#9ca3af"
                  >
                    {label || 'Untitled'}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {!loading && !error && (
        <DailyStatsChart userId={userId} metric={metric} from={dailyRangeFrom} />
      )}

      {!loading && !error && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 8px 6px 0',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <div>Title</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Categories</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Tags</div>
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 8px 6px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="Total times this ad was shown on screen."
              >
                Views
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 8px 6px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="Total times viewers clicked through from this ad."
              >
                Clicks
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 8px 6px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="Clicks driven from social shares of this ad."
              >
                Social clicks
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 8px 6px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="Social channel that produced the most clicks for this ad."
              >
                Top social source
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 8px 6px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="Total telemetry samples analyzed for this ad (camera + speed readings)."
              >
                Telemetry samples
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 8px 6px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="How many times this ad was hidden for safety (e.g. high speed or risky conditions)."
              >
                Safety blanks
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 0 6px 8px',
                  borderBottom: '1px solid #e5e7eb',
                }}
                title="Relative share of the selected metric compared with the top ad in this list."
              >
                {metric === 'views'
                  ? 'Views share'
                  : metric === 'clicks'
                    ? 'Clicks share'
                    : 'Blanks share'}
              </th>
              <th style={{ width: 80, borderBottom: '1px solid #e5e7eb' }} />
            </tr>
          </thead>
          <tbody>
            {filteredAds.map((ad, index) => {
              let topSourceLabel = '-';
              const social = ad.analytics?.social;
              const bySource = social?.bySource || {};

              const entries = Object.entries(bySource) as [string, number][];
              if (entries.length > 0) {
                const [bestSource, bestCount] = entries.reduce(
                  (maxEntry, current) => (current[1] > maxEntry[1] ? current : maxEntry),
                  entries[0],
                );
                topSourceLabel = `${bestSource} (${bestCount})`;
              }

              return (
                <tr
                  key={ad._id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f9fafb' : '#ffffff',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <td
                    style={{
                      paddingTop: 8,
                      paddingBottom: 8,
                      paddingRight: 8,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{ad.title || 'Untitled'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>
                      <span style={{ fontWeight: 500 }}>Categories:&nbsp;</span>
                      {ad.categories?.length ? ad.categories.join(', ') : 'None'}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      <span style={{ fontWeight: 500 }}>Tags:&nbsp;</span>
                      {ad.tags?.length ? ad.tags.join(', ') : 'None'}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    {typeof ad.views === 'number' ? ad.views : 0}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    {typeof ad.clicks === 'number' ? ad.clicks : 0}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    {ad.analytics?.social?.totalClicks ?? 0}
                  </td>
                  <td style={{ padding: '8px' }}>{topSourceLabel}</td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    {ad.analytics?.telemetrySamples ?? 0}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    {ad.analytics?.telemetryBlankEvents ?? 0}
                  </td>
                  <td style={{ padding: '8px 0 8px 8px' }}>
                    <div
                      style={{
                        position: 'relative',
                        width: 140,
                        height: 10,
                        borderRadius: 9999,
                        backgroundColor: '#e5e7eb',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width:
                            maxMetricValue > 0
                              ? `${(metricValue(ad) / maxMetricValue) * 100}%`
                              : '0%',
                          backgroundColor:
                            metric === 'telemetryBlankEvents' ? '#ef4444' : '#3b82f6',
                        }}
                      />
                    </div>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    <Link
                      to={`/admin/ads/${ad._id}`}
                      style={{
                        fontSize: 11,
                        color: '#2563eb',
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                      }}
                    >
                      Details
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Analytics;
